import {
  AggFunction,
  BaseMetricDraft,
  ColumnInfo,
  ColumnsResponse,
  FilterCandidate,
  InferenceResult,
  ParamType,
} from './types';

/**
 * Week 4 auto-inference engine (CLIENT-SIDE ONLY).
 *
 * Pure and synchronous: no network, no AWS, no I/O. Takes the parsed
 * ColumnsResponse (from ddlParser.ts) and proposes a draft tool config:
 * a tool name, a category, which columns are dates / metrics / filters, and
 * a SQL-type -> parameter-type map. The wizard UI (Agent 2) renders this as a
 * pre-filled form the user can review and edit.
 *
 * `inferConfig` is part of the LOCKED Week 4 contract — do not change its
 * signature.
 */

// ─────────────────────────────────────────────────────────────────────────
// Tuning constants
// ─────────────────────────────────────────────────────────────────────────

/**
 * A `character varying` column is treated as a categorical FILTER (e.g. a
 * dropdown of choices) only if it has at most this many distinct values.
 * Anything above this is assumed to be free text (names, descriptions, ids)
 * and is left as a plain string parameter, not a filter.
 */
const LOW_CARDINALITY_MAX = 50;

/**
 * Default aggregation applied to every inferred numeric metric. SUM is the
 * most common choice for finance facts (revenue, units, cost). The user can
 * change it per-metric in the wizard.
 */
const DEFAULT_AGG: AggFunction = 'SUM';

// ─────────────────────────────────────────────────────────────────────────
// Type classification sets (all values are the NORMALIZED dataType strings
// that ddlParser.ts emits — e.g. 'character varying', not 'varchar')
// ─────────────────────────────────────────────────────────────────────────

/**
 * Numeric column types that become metric candidates. These match the
 * normalized outputs of ddlParser's TYPE_NORMALIZATION table. `smallint` is
 * included because it is just a small integer; `decimal` already normalizes
 * to `numeric`, and `float*` variants normalize to `real`/`double precision`.
 */
const NUMERIC_TYPES = new Set<string>([
  'numeric',
  'integer',
  'bigint',
  'smallint',
  'double precision',
  'real',
]);

/** Text column types eligible to become categorical filters / string params. */
const TEXT_TYPES = new Set<string>(['character varying', 'character']);

/**
 * Leading table-name tokens that carry no business meaning and are stripped
 * before deriving the tool name. (rpt = report, meta = metadata, bluey = the
 * platform namespace. dim/fact/agg/stg/tmp/vw are common warehouse prefixes.)
 */
const NAME_PREFIXES = new Set<string>([
  'rpt',
  'meta',
  'bluey',
  'dim',
  'fact',
  'agg',
  'stg',
  'tmp',
  'vw',
]);

/**
 * Category keyword -> category label. Order matters: the first keyword found
 * in the table/schema/column tokens wins. Defaults to 'GENERAL' if none match.
 */
const CATEGORY_KEYWORDS: Array<{ token: string; category: string }> = [
  { token: 'pnl', category: 'PNL' },
  { token: 'tcpu', category: 'TCPU' },
  { token: 'bom', category: 'BOM' },
  { token: 'axis', category: 'AXIS' },
];
const DEFAULT_CATEGORY = 'GENERAL';

// ─────────────────────────────────────────────────────────────────────────
// Small string helpers
// ─────────────────────────────────────────────────────────────────────────

/** Splits an identifier into lowercase word tokens (handles _, -, spaces, camelCase). */
export function tokenize(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary -> space
    .split(/[^a-zA-Z0-9]+/) // split on any non-alphanumeric run
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length > 0);
}

/** Capitalizes the first letter of a token, lowercasing the rest. 'pnl' -> 'Pnl'. */
function capitalize(token: string): string {
  if (token.length === 0) return token;
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

/** Converts an identifier to snake_case. 'productLineName' / 'ProductLine' -> 'product_line_name'. */
export function toSnakeCase(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // camelCase boundary
    .replace(/[^a-zA-Z0-9]+/g, '_') // non-alphanumeric -> _
    .replace(/_+/g, '_') // collapse repeats
    .replace(/^_+|_+$/g, '') // trim leading/trailing _
    .toLowerCase();
}

// ─────────────────────────────────────────────────────────────────────────
// Duty 1 — tool name
// ─────────────────────────────────────────────────────────────────────────

/**
 * Derives a camelCase `getXxx` tool name from a raw table name.
 *
 * RULE (deterministic):
 *   1. Tokenize the table name on underscores / non-alphanumerics.
 *   2. Drop leading "noise" prefix tokens (rpt, meta, bluey, dim, fact, ...).
 *   3. Drop trailing version tokens: `v<digits>` (e.g. v3) or pure-digit tokens.
 *   4. PascalCase each surviving token and prepend "get".
 *   5. If nothing survives, fall back to "getData".
 *
 * Example: `rpt_bluey_prophecy_pnl_v3`
 *   tokens        -> [rpt, bluey, prophecy, pnl, v3]
 *   strip prefix  -> [prophecy, pnl, v3]
 *   strip version -> [prophecy, pnl]
 *   result        -> "getProphecyPnl"
 *
 * NOTE on the timeline doc's `... -> getPnlRevenue` example: "Revenue" is not
 * present anywhere in the table name, so it cannot be produced mechanically —
 * that example is aspirational (it injects domain knowledge). Our deterministic
 * rule yields `getProphecyPnl`, which the user can rename in the wizard.
 */
function deriveToolName(tableName: string): string {
  let tokens = tokenize(tableName);

  // Step 2: strip leading noise prefixes (repeatedly).
  while (tokens.length > 0 && NAME_PREFIXES.has(tokens[0])) {
    tokens = tokens.slice(1);
  }

  // Step 3: strip trailing version/number tokens (repeatedly).
  while (
    tokens.length > 0 &&
    /^(v\d+|\d+)$/.test(tokens[tokens.length - 1])
  ) {
    tokens = tokens.slice(0, -1);
  }

  if (tokens.length === 0) return 'getData';

  return 'get' + tokens.map(capitalize).join('');
}

// ─────────────────────────────────────────────────────────────────────────
// Duty 2 — category
// ─────────────────────────────────────────────────────────────────────────

/**
 * Infers a category from naming patterns. Scans tokens from the schema, table
 * name, AND column names; returns the first matching keyword's label, else
 * the default. Token-level matching avoids false hits (e.g. 'bom' will not
 * match inside 'bottom').
 */
function inferCategory(columns: ColumnInfo[], tableName: string, schema: string | null): string {
  const tokenSet = new Set<string>();
  for (const t of tokenize(tableName)) tokenSet.add(t);
  if (schema) for (const t of tokenize(schema)) tokenSet.add(t);
  for (const col of columns) for (const t of tokenize(col.columnName)) tokenSet.add(t);

  for (const { token, category } of CATEGORY_KEYWORDS) {
    if (tokenSet.has(token)) return category;
  }
  return DEFAULT_CATEGORY;
}

// ─────────────────────────────────────────────────────────────────────────
// Per-column predicates
// ─────────────────────────────────────────────────────────────────────────

export function isDateColumn(col: ColumnInfo): boolean {
  const dt = col.dataType.toLowerCase();
  return dt === 'date' || dt.startsWith('timestamp');
}

export function isNumericColumn(col: ColumnInfo): boolean {
  return NUMERIC_TYPES.has(col.dataType.toLowerCase());
}

/**
 * Identifier columns (e.g. `id`, `product_id`, `product_line_id`) are numeric
 * keys, not measurable quantities — SUM/AVG on them is meaningless. We exclude
 * them from base-metric candidates.
 *
 * Detection is TOKEN-based, not substring-based: a column qualifies if one of
 * its word tokens is exactly `id` (so `product_line_id`, `productId`, `id` all
 * match) — but NOT words that merely contain the letters "id" like `width`,
 * `valid`, `video`, or `paid`. This avoids false exclusions.
 */
export function isIdentifierColumn(col: ColumnInfo): boolean {
  return tokenize(col.columnName).includes('id');
}

export function isTextColumn(col: ColumnInfo): boolean {
  return TEXT_TYPES.has(col.dataType.toLowerCase());
}

/**
 * A text column is a low-cardinality filter candidate if we have EVIDENCE it
 * has few distinct values:
 *   - distinctValues is known and within [1, LOW_CARDINALITY_MAX], OR
 *   - distinctValues is unknown but sampleValues is a small non-empty list.
 *
 * DDL pasted with no data has both fields null, so it yields no filters —
 * cardinality is genuinely unknown there and the user picks filters manually.
 */
export function isLowCardinalityText(col: ColumnInfo): boolean {
  if (!isTextColumn(col)) return false;
  if (col.distinctValues !== null) {
    return col.distinctValues > 0 && col.distinctValues <= LOW_CARDINALITY_MAX;
  }
  if (col.sampleValues !== null) {
    return col.sampleValues.length > 0 && col.sampleValues.length <= LOW_CARDINALITY_MAX;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// Duty 6 — SQL type -> parameter type
// ─────────────────────────────────────────────────────────────────────────

/**
 * Maps a column to a wizard ParamType.
 *   numeric*            -> 'number'
 *   boolean             -> 'boolean'
 *   date / timestamp*   -> 'array'  (a date RANGE is modeled as [start, end])
 *   character varying   -> 'array'  if it's a low-cardinality filter (multi-select enum)
 *                          'string' otherwise (free text)
 *   anything else       -> 'string' (safe default for unknown types)
 */
export function mapParamType(col: ColumnInfo, isFilter: boolean): ParamType {
  if (isNumericColumn(col)) return 'number';
  if (col.dataType.toLowerCase() === 'boolean') return 'boolean';
  if (isDateColumn(col)) return 'array';
  if (isTextColumn(col)) return isFilter ? 'array' : 'string';
  return 'string';
}

// ─────────────────────────────────────────────────────────────────────────
// Main entry point
// ─────────────────────────────────────────────────────────────────────────

export function inferConfig(columns: ColumnsResponse): InferenceResult {
  const cols = columns.columns ?? [];

  const toolName = deriveToolName(columns.tableName);
  const category = inferCategory(cols, columns.tableName, columns.schema);

  // Duty 3 — date columns.
  const dateColumns: string[] = cols.filter(isDateColumn).map((c) => c.columnName);

  // Duty 4 — numeric columns become base-metric candidates, EXCEPT identifier
  // columns (e.g. *_id), which are numeric keys, not measurable quantities.
  const metricCandidates: BaseMetricDraft[] = cols
    .filter((c) => isNumericColumn(c) && !isIdentifierColumn(c))
    .map((c) => ({
      metricKey: toSnakeCase(c.columnName),
      exprSql: c.columnName,
      agg: DEFAULT_AGG,
      filterCondition: null,
    }));

  // Duty 5 — low-cardinality text columns become categorical filters.
  const filterCandidates: FilterCandidate[] = cols.filter(isLowCardinalityText).map((c) => ({
    columnName: c.columnName,
    // Prefer real sample values as the enum; if we only know the count (no
    // samples), leave enumValues null so the UI can fetch/let the user fill them.
    enumValues: c.sampleValues !== null && c.sampleValues.length > 0 ? c.sampleValues : null,
  }));

  // Duty 6 — every column gets a parameter type. A column is "a filter" here if
  // it qualified as a low-cardinality text filter above.
  const filterColumnNames = new Set(filterCandidates.map((f) => f.columnName));
  const parameterTypeMap: Record<string, ParamType> = {};
  for (const c of cols) {
    parameterTypeMap[c.columnName] = mapParamType(c, filterColumnNames.has(c.columnName));
  }

  return {
    toolName,
    category,
    dateColumns,
    metricCandidates,
    filterCandidates,
    parameterTypeMap,
  };
}
